import { useEffect, useState } from "react";
import { Modal, Input, Button, message, Divider } from "antd";
import { CopyOutlined } from "@ant-design/icons";
import { useMutation } from "@apollo/client";
import CreateTrackerScanner from "./CreateTrackerScanner";
import { CREATE_TRACKER } from "../graphql/queries";
import { CreateTrackerScanResult, parseCreateTrackerScan } from "../utils/createTrackerScanParser";

interface CreateTrackerModalProps {
  open: boolean;
  onClose: () => void;
}

export default function CreateTrackerModal({ open, onClose }: CreateTrackerModalProps) {
  const [showScanner, setShowScanner] = useState(false);
  const [trackerId, setTrackerId] = useState("");
  const [extraParts, setExtraParts] = useState<string[]>([]);
  const [caseId, setCaseId] = useState("");
  const [telNumber, setTelNumber] = useState("");

  const [createTracker, { loading: submitting }] = useMutation<{
    createTracker: {
      success: boolean;
      message: string;
      tracker?: { id: string; caseid: string; tel_number?: string };
    };
  }>(CREATE_TRACKER);

  useEffect(() => {
    if (!open) {
      setShowScanner(false);
      setTrackerId("");
      setExtraParts([]);
      setCaseId("");
      setTelNumber("");
    }
  }, [open]);

  const handleClose = () => {
    onClose();
  };

  const applyScanResult = (result: CreateTrackerScanResult) => {
    setTrackerId(result.trackerId);
    setExtraParts(result.extraParts);
    setShowScanner(false);
    message.success("Tracker ID doplněn ze skenu.");
  };

  const handleScan = (result: CreateTrackerScanResult) => {
    applyScanResult(result);
  };

  const handleManualTrackerBlur = () => {
    const raw = trackerId.trim();
    if (!raw.includes(";")) {
      return;
    }
    const parsed = parseCreateTrackerScan(raw);
    if (parsed) {
      setTrackerId(parsed.trackerId);
      setExtraParts(parsed.extraParts);
    }
  };

  const handleCopy = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      message.success("Zkopírováno.");
    } catch {
      message.error("Kopírování se nezdařilo.");
    }
  };

  const handleSubmit = async () => {
    const resolvedTrackerId = trackerId.trim();
    if (!resolvedTrackerId || !caseId.trim()) {
      return;
    }
    try {
      const { data: res } = await createTracker({
        variables: {
          input: {
            id: resolvedTrackerId,
            caseId: caseId.trim(),
            telNumber: telNumber.trim() || null,
          },
        },
      });
      if (res?.createTracker?.success) {
        message.success(res.createTracker.message || "Tracker byl vytvořen.");
        handleClose();
      } else {
        message.error(res?.createTracker?.message || "Chyba při vytváření trackeru.");
      }
    } catch (e) {
      console.error(e);
      message.error("Chyba při vytváření trackeru.");
    }
  };

  const canSubmit = Boolean(trackerId.trim() && caseId.trim());

  return (
    <>
      <CreateTrackerScanner
        visible={open && showScanner}
        onClose={() => setShowScanner(false)}
        onScan={handleScan}
      />

      <Modal
        title="Vytvořit tracker"
        open={open && !showScanner}
        onCancel={handleClose}
        footer={[
          <Button key="cancel" onClick={handleClose}>
            Zrušit
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={submitting}
            disabled={!canSubmit}
            onClick={() => void handleSubmit()}
          >
            Vytvořit tracker
          </Button>,
        ]}
        destroyOnClose
        centered
      >
        {extraParts.length > 0 ? (
          <div style={{ marginBottom: 16 }}>
            <div style={{ marginBottom: 8, fontWeight: 500 }}>Další hodnoty ze skenu</div>
            {extraParts.map((part, index) => (
              <div
                key={`${part}-${index}`}
                style={{ display: "flex", gap: 8, marginBottom: 8, alignItems: "center" }}
              >
                <Input value={part} readOnly style={{ flex: 1 }} />
                <Button icon={<CopyOutlined />} onClick={() => void handleCopy(part)}>
                  Kopírovat
                </Button>
              </div>
            ))}
          </div>
        ) : null}

        <div style={{ marginBottom: 16 }}>
          <div style={{ marginBottom: 8, fontWeight: 500 }}>Tracker ID</div>
          <Button block onClick={() => setShowScanner(true)} style={{ marginBottom: 12 }}>
            Naskenovat QR
          </Button>
          <Divider plain style={{ margin: "12px 0" }}>
            nebo zadat ručně
          </Divider>
          <Input
            value={trackerId}
            onChange={(e) => {
              setTrackerId(e.target.value);
              if (extraParts.length > 0) {
                setExtraParts([]);
              }
            }}
            onBlur={handleManualTrackerBlur}
            placeholder="Zadejte tracker ID"
          />
        </div>

        <div style={{ marginBottom: 16 }}>
          <div style={{ marginBottom: 6, fontWeight: 500 }}>Case ID</div>
          <Input
            value={caseId}
            onChange={(e) => setCaseId(e.target.value)}
            placeholder="Zadejte case ID"
          />
        </div>
        <div>
          <div style={{ marginBottom: 6, fontWeight: 500 }}>Telefonní číslo</div>
          <Input
            value={telNumber}
            onChange={(e) => setTelNumber(e.target.value)}
            placeholder="Volitelné telefonní číslo"
            type="tel"
          />
        </div>
      </Modal>
    </>
  );
}
